import List from './list';
import {
  Link,
  useLocation
} from 'react-router-dom';
import {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { t } from '@lingui/core/macro';
import { elementIsVisibleInViewport, Tooltip } from '@thermon-platforms/thermon-ui-core';
import { getDeviceUrl, isDuo } from 'util/device';
import useAlarmStore from 'store/alarms';
import useDeviceStore from 'store/devices';
import StatusIcon from 'components/status-icon';


function DevicesListItem({device, path = [], showChildren=true}) {
  const [devicesStore] = useDeviceStore();
  const [alarmsStore] = useAlarmStore();
  // Create a ref to store our root element
  const rootRef = useRef(null);
  const location = useLocation();
  // Pretty stupid and simple way to default the tree to open to the curent device
  const [expanded, setExpanded] = useState(
    location?.pathname?.includes(device.model.id) ||
    (device?.model?.type == 'Tag' ?  device.model.children?.some(c => location?.pathname?.includes(c.id)) : false)
  );
  const isTagDevice = device.model.type == 'Tag';
  const showStatusIcon = !isTagDevice;
  const type = isDuo(device.model.externalId) ? 'duo' : device?.model?.type?.toLowerCase();
  const unassigned = Object.prototype.hasOwnProperty.call(device.model, 'assigned') && device.model.assigned === false;

  const url = isTagDevice ? device.model.id : getDeviceUrl(device);
  // If location changes let's see if we need to
  useEffect(() => {
    // 1. Expand our device hierarchy to show this node
    if (!expanded) setExpanded(location?.pathname?.includes(device.model.id));
    // 2. Scroll the list so the current device is visible
    if (location?.pathname?.endsWith(device.model.id)) {
      if (elementIsVisibleInViewport(rootRef?.current?.firstChild, true)) return;
      rootRef.current?.firstChild?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [location]);

  const alarms = useMemo(() => {
    if (isTagDevice || !alarmsStore?.alarms?.length) {
      return [];
    }

    const shallowDevices = devicesStore.shallowDevices;

    // Derive alarm status from store data without triggering an extra render.
    return alarmsStore.alarms.filter((alarm) => {
      if (!alarm.deviceId) return false;
      // If alarm is on this device add it
      if (alarm.deviceId == device.model.id) return true;
      // If this device is in the path of the device with the alarm add it
      const alarmPath = shallowDevices.get(alarm.deviceId)?.path;
      if (!alarmPath) return false;
      return alarmPath.some((d) => d.id == device.model.id);
    });
  }, [isTagDevice, alarmsStore.alarms, device.model.id, devicesStore.shallowDevices]);

  return (
    <li
      ref={rootRef}
      className={`${type} ${expanded ? 'expanded' : ''}`}
      onDoubleClick={(event) => {
        event.stopPropagation();
        setExpanded(!expanded);
      }}
    >
      <Link
        to={url}
        style={{paddingLeft: `${showChildren ? ((path.length * 12) + (!device?.model?.children?.length ? 36 : 15)) : 15}px`}}
        className={`${unassigned ? 'unassigned' : ''} ${url == location.pathname || (device.model.type == 'Tag' && location.pathname.endsWith(device.model.id)) ? 'active' : ''}`}
      >
        {/** If the device has no children don't display expand button */}
        {showChildren && device?.model?.children?.length ?
          (<span className="expand"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            {expanded ? 'Collapse' : 'Expand'}
          </span>) : <></>
        }
        <span className={`device-icon ${type}`}></span>
        {device.model.name}
        {showStatusIcon ? <StatusIcon
          device={device}
          alarms={alarms}
          showOkay={false}
        /> : <></>}
      </Link>
      {/** If this device has children, let's continue compiling our list */}
      {showChildren && device?.model?.children?.length && expanded ?
        <List devices={isDuo(device.model.externalId) ?
          (device.model.children[0]?.children[0]?.children ?? []) :
          [...device.model.children].sort((a, b) => {
            return (a?.name || '').localeCompare((b?.name || ''));
          })} path={[...path, device]}
        /> : <></>
      }
    </li>
  )
}

export default memo(DevicesListItem);
